/**
 * Shared PGF Standard team tile - PGF Draft Board Sweep, Item 6.
 * Supersedes HF-TD47-2 (2-line webkit clamp) and TD-47.6a-ui v1.4 Redline 1
 * (char-count font-size tiers). [Stabilization fix - Feb 2026] Also
 * supersedes the shared `useAutoFitText` JS hook for THIS tile - that
 * hook mis-measured the tile's auto-height wrapper and produced
 * invisible/exploded team names. Team name sizing here is a bounded CSS
 * `clamp()` keyed to the tile's own container-inline-size (`cqi`), which
 * cannot collapse to 0 or explode, with up to 2 lines for long names.
 *
 * Extracted from auction's TeamTile + snake's ManagerTile (near-identical
 * twins). Both StandaloneDraftBoard.jsx (auction) and
 * SnakeStandaloneDraftBoard.jsx (snake) consume this component.
 *
 * Format-specific pieces stay behind explicit props, never behind format
 * detection inside this file:
 *  - Auction-only: showBudget, maxBid (restored per founder Follow-up 1 -
 *    Feb 2026; bottom-right corner, budget stays bottom-left),
 *    currentBid/currentBidderId/positionColor (bid fireworks),
 *    previousBidderId/previousBid/prevFading (prev-bid fade).
 *  - Snake-only: slot (test-id fallback only since the "SLOT N" label was
 *    removed at founder request), onClock, onDeck (pills).
 *
 * Manager first-name display is LIVE code behind `showManagerFirstName`
 * (defaulted OFF per the approved mock, which hides it) rather than
 * commented-out dead code, per the no-dead-code standing order.
 */
import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import ManagerName from "../../ManagerName";
import { PICK_CELL_MIN_H } from "./PlayerCell";

export default function TeamTile({
  manager,
  isDark,
  showBudget = false,
  maxBid = null,
  showManagerFirstName = false,
  currentBid = null,
  currentBidderId = null,
  positionColor = null,
  bidSpanStyle = null,
  bidClassName = "",
  previousBidderId = null,
  previousBid = null,
  prevFading = false,
  slot = null,
  onClock = false,
  onDeck = false,
}) {
  const uid = manager?.user_id || slot;
  const isHighBidder = !!currentBidderId && manager?.user_id === currentBidderId;

  const tileBg = isDark ? "#2A2A2A" : "#FFFFFF";
  const tileBorder = isDark ? "#3A3A3A" : "#D0D0D0";
  const nameColor = isDark ? "#F5F5F5" : "#0A0A0A";
  const subColor = isDark ? "rgba(255,255,255,0.50)" : "rgba(0,0,0,0.45)";

  // [Stabilization fix - Feb 2026] The JS measure-and-fit hook mis-measured
  // this box (the old JS hook's call took a ratio of 20 against
  // `el.parentElement` - the tile's auto-height wrapper, not a stable
  // dimension - producing a 0-20x container-height starting font size:
  // invisible or exploded depending on the tile's transient layout height).
  // Replaced with a bounded CSS clamp keyed to the tile's own inline-size
  // (grid/flex-determined, never circular) - cannot collapse to 0, cannot
  // explode, and up to 2 lines so long team names wrap instead of vanish.

  const ringStyle = onClock
    ? {
        boxShadow:
          "0 0 0 2px rgba(60,210,80,1), 0 0 22px rgba(60,210,80,0.55), 0 0 36px rgba(60,210,80,0.30)",
        animation: "pgf-pulse 1.4s ease-in-out infinite",
      }
    : onDeck
    ? { boxShadow: "0 0 0 2px rgba(255,170,90,0.85), 0 0 14px rgba(255,170,90,0.35)" }
    : {};

  return (
    <div
      className="rounded-xl border p-3 relative overflow-hidden sticky top-0 z-20"
      style={{
        background: tileBg,
        borderColor: tileBorder,
        containerType: "inline-size",
        // [Board Tile Polish item 1] Same height as a player card, off the
        // one shared constant. The extra room is what lets a multi-word
        // team name wrap to as many lines as it needs.
        // WARNING: minHeight, never height/maxHeight. The root carries
        // `overflow-hidden` for the glow ring, so pinning a height here
        // would silently clip a wrapped name like "RED ZONE RENEGADES".
        minHeight: PICK_CELL_MIN_H,
        ...ringStyle,
      }}
      data-testid={`board-mgr-tile-${uid}`}
    >
      {/* [PGF Board Tile Fixes item 3] The "SLOT N" label was removed at
          founder request. `slot` remains only as the id fallback for the
          test id when a manager row hasn't hydrated yet. ON CLOCK / ON
          DECK pills are unchanged. */}
      <div
        className="font-display font-black uppercase w-full text-center"
        style={{
          color: nameColor,
          lineHeight: 1.05,
          // [Board Tile Polish item 1] Larger, and no line clamp / max
          // height / overflow cap: "RED ZONE RENEGADES" wraps to as many
          // lines as it needs and stays FULLY visible. `normal` wrapping
          // breaks at spaces (never mid-word) now that the taller tile
          // gives it the room.
          fontSize: "clamp(14px, 11cqi, 28px)",
          minHeight: 22,
          overflowWrap: "normal",
          wordBreak: "normal",
          hyphens: "none",
        }}
      >
        <ManagerName
          name={manager?.team_name || "-"}
          isBot={!!manager?.is_bot}
          iconSize={12}
          testId={`board-mgr-${uid}`}
        />
      </div>

      {showManagerFirstName && (
        <p className="font-body text-xs mb-1.5 truncate" style={{ color: subColor }}>
          {manager?.first_name || "-"}
        </p>
      )}

      {showBudget && (
        <p className="font-display font-black" style={{ fontSize: 16, color: "#FF6B00" }}>
          ${manager?.budget ?? 0}
        </p>
      )}

      {/* Max bid - auction-only, bottom-right corner. Budget (above) stays
          bottom-left per founder Follow-up 1 restore (Feb 2026). */}
      {showBudget && maxBid != null && (
        <span
          className="absolute right-2 bottom-1.5 font-body text-[10px] font-bold"
          style={{ color: subColor }}
          data-testid={`board-max-bid-${uid}`}
        >
          MAX BID ${maxBid}
        </span>
      )}

      {onClock && (
        <div
          className="text-[10px] font-display font-black uppercase tracking-widest"
          style={{ color: "rgb(60,210,80)" }}
          data-testid={`board-on-clock-${uid}`}
        >
          ● ON CLOCK
        </div>
      )}
      {onDeck && !onClock && (
        <div
          className="text-[10px] font-display font-bold uppercase tracking-widest"
          style={{ color: "rgb(255,170,90)" }}
          data-testid={`board-on-deck-${uid}`}
        >
          ◐ ON DECK
        </div>
      )}

      {/* Bid fireworks - BAM punch, auction-only (gated by currentBid + positionColor being provided). */}
      <AnimatePresence>
        {isHighBidder && currentBid && positionColor && (
          <motion.div
            key={`${uid}-${currentBid}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.2 } }}
            transition={{ duration: 0.15 }}
            className="absolute inset-0 flex items-center justify-center rounded-xl pointer-events-none"
            style={{ background: `${positionColor}18` }}
          >
            <motion.span
              key={`bid-glyph-${currentBid}`}
              data-testid={`bid-fireworks-${uid}`}
              className={`font-display font-black select-none ${bidClassName}`}
              style={bidSpanStyle || undefined}
              initial={{ scale: 0.25 }}
              animate={{ scale: [0.25, 2.6, 0.92, 1.0] }}
              transition={{
                duration: 1.0,
                times: [0, 0.22, 0.50, 1.0],
                ease: [0.34, 1.56, 0.64, 1],
              }}
            >
              ${currentBid}
            </motion.span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Previous-bidder gray fade - auction-only. */}
      {previousBidderId === manager?.user_id && previousBid && !isHighBidder && (
        <div
          data-testid={`prev-bid-gray-${uid}`}
          className="absolute bottom-2 right-2 pointer-events-none transition-opacity duration-1000"
          style={{ color: isDark ? "#666" : "#999", opacity: prevFading ? 0 : 1 }}
        >
          <span className="font-display font-black text-lg leading-none">${previousBid}</span>
        </div>
      )}
    </div>
  );
}
